<!-- The app's ONE navigation bar. Factory-owned.

     THE PROBLEM THIS SOLVES. Nothing in the chassis used to style or shape a
     toolbar, so every screen answered the question itself: one app came out of
     a single build with six toolbar patterns across twenty-four screens — some
     with a back button and some without, two printing their own name twice, one
     using its title as a live status field, one with no bar at all. Every
     individual screen looked fine. The defect only exists in the aggregate, and
     nothing in the pipeline ever looks at twenty-four screens at once.

     THE CONTRACT — two tiers, and every screen is one of them:

       a tab root        this bar, plus <AppLargeTitle> inside the content
       a pushed screen   this bar alone — back button, the screen's name, and
                         AT MOST ONE trailing action

     THE TITLE IS THE THING, NEVER THE STATE. A bar bound to booking status put
     "On the way" where the screen's name should be: the screen had no name, the
     name changed under the customer while they read it, and the back stack's
     label changed with it. The bar says what you are looking at; the content
     says what is happening to it.

     A SCREEN PRINTS ITS NAME ONCE. A tab root with a large title does not also
     carry an <h1>. A pushed screen carries a content heading only when it says
     something the bar does not.

     THE HAIRLINE IS EARNED. iOS draws a separator under a bar only once content
     has scrolled beneath it, and Ionic manages that itself for the iOS condense
     case and nowhere else. This listens to the page's own scroll element, so
     the rule is the same on a pushed screen as on a tab root, and the same on
     Android as on iOS. -->
<template>
  <!-- `app-header--root` says the start slot is empty, which is what lets
       app.css pull the title to the start. With a back or menu button sitting
       there, a start-aligned title prints straight on top of it. -->
  <IonHeader
    ref="host"
    class="app-header"
    :class="{ 'app-header--scrolled': scrolled, 'app-header--root': !back && !menu }"
    :translucent="true"
  >
    <IonToolbar>
      <!-- Back OR the menu button, never both: they occupy the same corner and
           mean opposite things. A pushed screen goes back; a sidebar
           destination opens the menu. Ionic hides `IonMenuButton` by itself
           once the split pane is showing, so it is correct at every width. -->
      <IonButtons v-if="back" slot="start">
        <IonBackButton :default-href="back" :text="backText" />
      </IonButtons>
      <IonButtons v-else-if="menu" slot="start">
        <IonMenuButton />
      </IonButtons>

      <!-- THE BAR'S TITLE IS THE SCREEN'S h1, and it has to say so out loud.
           `ion-title` renders a plain `<div class="toolbar-title">` in its
           shadow root with no heading role at all, and a tab root carries no
           content heading on purpose (a screen prints its name once) — so the
           busiest screen in the app had no h1 and "jump by heading" landed
           nowhere. The role goes on the host because the shadow child is not
           ours to mark up. `AppLargeTitle` deliberately does NOT do this: two
           of them on one screen would be two h1s. -->
      <IonTitle role="heading" aria-level="1">{{ title }}</IonTitle>

      <!-- At most one action, and the page supplies it as
           `<IonButtons slot="end">`. The DEFAULT slot, not a named one: the
           `slot="end"` the page writes is an attribute for ION-TOOLBAR's own
           slot, not a name for this component's. Asking for `name="end"` here
           matches nothing and silently drops the action, with no error
           anywhere. Rendering it as a direct child of the toolbar is what puts
           it in the toolbar's end slot. -->
      <slot />
    </IonToolbar>
  </IonHeader>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonBackButton, IonMenuButton,
} from '@ionic/vue';

defineProps({
  /** What you are looking at. A noun. Never a status, never a question. */
  title: { type: String, default: '' },
  /** Where Back goes when there is no history to pop. Omit on a tab root. */
  back: { type: String, default: '' },
  /** iOS shows a word beside the chevron; Material shows the chevron alone. */
  backText: { type: String, default: 'Back' },
  /** Sidebar shell only: a destination screen opens the menu instead of going
   *  back. Ignored when `back` is set — one corner, one meaning. */
  menu: { type: Boolean, default: false },
});

const host = ref(null);
const scrolled = ref(false);

let scrollEl = null;

const onScroll = () => {
  // A threshold rather than `> 0`: iOS rubber-bands past zero on every touch,
  // and a hairline that blinks on a bounce is worse than no hairline.
  scrolled.value = (scrollEl?.scrollTop ?? 0) > 4;
};

onMounted(async () => {
  // The header is a SIBLING of the content inside `ion-page`, so the page is
  // the only reliable common ancestor. `closest()` survives a wrapper being
  // added later in a way `parentElement` does not.
  const el = host.value?.$el;
  const page = el?.closest?.('.ion-page') ?? el?.parentElement;
  const content = page?.querySelector?.('ion-content');
  if (!content?.getScrollElement) return;

  // A native scroll listener rather than Ionic's `ionScroll`, so the page does
  // not have to opt into `scroll-events` — which fires a Stencil event per
  // frame for the whole app to pay for one hairline.
  scrollEl = await content.getScrollElement();
  scrollEl.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
});

onBeforeUnmount(() => {
  scrollEl?.removeEventListener('scroll', onScroll);
  scrollEl = null;
});
</script>
